/** Selector 등 포털+fixed 패널 공통 위치 계산 */

export const DROPDOWN_GAP = 4;
export const VIEWPORT_MARGIN = 8;
export const DROPDOWN_CONTENT_MIN_WIDTH = 120;
export const SEARCH_DETAIL_DRAWER_SELECTOR = ".search-detail-drawer";

const OVERFLOW_CLIP_VALUES = new Set(["hidden", "auto", "scroll", "clip", "overlay"]);

function isOverflowClipping(style) {
    const {overflow, overflowX, overflowY} = style;
    return (
        OVERFLOW_CLIP_VALUES.has(overflow) ||
        OVERFLOW_CLIP_VALUES.has(overflowX) ||
        OVERFLOW_CLIP_VALUES.has(overflowY)
    );
}

export function getSearchDetailDrawerEl(triggerEl) {
    return triggerEl?.closest?.(SEARCH_DETAIL_DRAWER_SELECTOR) ?? null;
}

export function getEffectiveRightBoundaryPx(triggerEl, viewportRight) {
    let right = viewportRight;
    let el = triggerEl.parentElement;
    while (el) {
        if (el === document.documentElement) break;
        const style = window.getComputedStyle(el);
        if (isOverflowClipping(style)) {
            right = Math.min(right, el.getBoundingClientRect().right);
        }
        el = el.parentElement;
    }
    return right;
}

function getViewportRightPx() {
    const vv = window.visualViewport;
    const vvRightSpan =
        vv != null && vv.width > 0 ? vv.offsetLeft + vv.width : Number.POSITIVE_INFINITY;
    return Math.min(
        window.innerWidth,
        document.documentElement?.clientWidth ?? window.innerWidth,
        vvRightSpan,
    );
}

/** 검색 드로어: 선택 버튼 왼쪽 고정 — 너비는 드로어에 묶지 않음 */
function alignLeftToTrigger(triggerRect, minLeftPx) {
    return Math.max(minLeftPx, triggerRect.left);
}

/** 컨텐츠 측정값 우선 — 고정 560px 같은 hint로 포털을 넓히지 않음 */
function resolveContentDropdownMinWidth(measuredW, dropdownMinWidth) {
    if (measuredW > 0) {
        return Math.max(DROPDOWN_CONTENT_MIN_WIDTH, measuredW);
    }
    const hint = dropdownMinWidth ?? 260;
    return Math.max(DROPDOWN_CONTENT_MIN_WIDTH, Math.min(hint, 360));
}

function computeDropdownInSearchDrawer({
    triggerEl,
    dropdownEl,
    containerEl,
    estimatedHeight,
    dropdownMinWidth,
}) {
    const triggerRect = triggerEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    const viewportRight = getViewportRightPx();

    let left = alignLeftToTrigger(triggerRect, VIEWPORT_MARGIN);
    const viewportMaxWidth = Math.max(
        DROPDOWN_CONTENT_MIN_WIDTH,
        viewportRight - VIEWPORT_MARGIN - left,
    );

    const measuredW = dropdownEl ? Math.ceil(dropdownEl.getBoundingClientRect().width) : 0;
    const minWidth = resolveContentDropdownMinWidth(measuredW, dropdownMinWidth);

    const ddHeight = dropdownEl?.offsetHeight
        ? Math.min(dropdownEl.offsetHeight, containerRect.height - VIEWPORT_MARGIN * 2)
        : Math.min(estimatedHeight, containerRect.height - VIEWPORT_MARGIN * 2);

    const spaceBelow = containerRect.bottom - triggerRect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = triggerRect.top - containerRect.top - VIEWPORT_MARGIN;
    const openUp = spaceBelow < ddHeight && spaceAbove > spaceBelow;

    let top;
    if (openUp && dropdownEl?.offsetHeight) {
        top = triggerRect.top - dropdownEl.offsetHeight - DROPDOWN_GAP;
    } else if (openUp) {
        top = triggerRect.top - ddHeight - DROPDOWN_GAP;
    } else {
        top = triggerRect.bottom + DROPDOWN_GAP;
    }
    top = Math.max(
        containerRect.top + VIEWPORT_MARGIN,
        Math.min(top, containerRect.bottom - VIEWPORT_MARGIN - (dropdownEl?.offsetHeight || ddHeight)),
    );

    const layoutW = dropdownEl?.offsetWidth || minWidth;
    if (layoutW > 0 && left + layoutW > viewportRight - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, viewportRight - VIEWPORT_MARGIN - layoutW);
    }

    return {
        top,
        left,
        inSearchDrawer: true,
        viewportMaxWidth,
        anchorWidth: Math.ceil(triggerRect.width),
    };
}

export function getDropdownPortalStyle(pos, {extendedPanel, defaultWidth, extendedWidth} = {}) {
    if (pos.inSearchDrawer || pos.contentSized) {
        const style = {
            top: pos.top,
            left: pos.left,
            maxWidth: pos.viewportMaxWidth,
            boxSizing: "border-box",
        };
        if (pos.inSearchDrawer && pos.anchorWidth > 0) {
            if (extendedPanel) {
                style.width = "max-content";
                style.minWidth = Math.max(pos.anchorWidth, 360);
            } else {
                style.width = pos.anchorWidth;
                style.minWidth = pos.anchorWidth;
            }
        } else {
            style.width = "max-content";
        }
        return style;
    }
    return {
        top: pos.top,
        left: pos.left,
        width: pos.width ?? (extendedPanel ? extendedWidth : defaultWidth),
    };
}

/** 검색 드로어 — CSS max-content 충돌 방지, 트리거 너비로 포털 고정 */
export function applySearchDrawerDropdownSizing(dropdownEl, triggerEl, pos, {widePanel = false} = {}) {
    if (!dropdownEl) return;

    if (!pos?.inSearchDrawer) {
        dropdownEl.style.removeProperty("--search-dropdown-anchor-width");
        dropdownEl.style.removeProperty("--search-dropdown-viewport-max");
        dropdownEl.removeAttribute("data-drawer-dropdown-wide");
        return;
    }

    const anchorW = pos.anchorWidth ?? Math.ceil(triggerEl?.getBoundingClientRect?.().width ?? 0);
    if (anchorW > 0) {
        dropdownEl.style.setProperty("--search-dropdown-anchor-width", `${anchorW}px`);
    }
    if (pos.viewportMaxWidth) {
        dropdownEl.style.setProperty("--search-dropdown-viewport-max", `${pos.viewportMaxWidth}px`);
    }

    if (widePanel) {
        dropdownEl.setAttribute("data-drawer-dropdown-wide", "true");
        dropdownEl.style.width = "max-content";
        dropdownEl.style.minWidth = `${Math.max(anchorW, 360)}px`;
    } else if (anchorW > 0) {
        dropdownEl.removeAttribute("data-drawer-dropdown-wide");
        dropdownEl.style.width = `${anchorW}px`;
        dropdownEl.style.minWidth = `${anchorW}px`;
    }
}

export function computeDropdownFixedPosition({
    triggerEl,
    dropdownEl,
    dropdownMinWidth,
    estimatedHeight,
    contentSized = false,
}) {
    const drawerEl = getSearchDetailDrawerEl(triggerEl);
    if (drawerEl) {
        return computeDropdownInSearchDrawer({
            triggerEl,
            dropdownEl,
            containerEl: drawerEl,
            estimatedHeight,
            dropdownMinWidth,
        });
    }

    const triggerRect = triggerEl.getBoundingClientRect();
    const viewportRight = getViewportRightPx();
    const layoutH = window.visualViewport?.height > 0
        ? window.visualViewport.height
        : window.innerHeight;

    const measuredW = dropdownEl ? Math.ceil(dropdownEl.getBoundingClientRect().width) : 0;
    const ddHeight = dropdownEl?.offsetHeight
        ? Math.min(dropdownEl.offsetHeight, layoutH - VIEWPORT_MARGIN * 2)
        : estimatedHeight;

    const spaceBelow = layoutH - triggerRect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = triggerRect.top - VIEWPORT_MARGIN;
    const openUp = spaceBelow < ddHeight && spaceAbove > spaceBelow;

    let top;
    if (openUp && dropdownEl?.offsetHeight) {
        top = triggerRect.top - dropdownEl.offsetHeight - DROPDOWN_GAP;
    } else if (openUp) {
        top = triggerRect.top - ddHeight - DROPDOWN_GAP;
    } else {
        top = triggerRect.bottom + DROPDOWN_GAP;
    }
    top = Math.max(
        VIEWPORT_MARGIN,
        Math.min(top, layoutH - VIEWPORT_MARGIN - (dropdownEl?.offsetHeight || ddHeight)),
    );

    const contentRight = getEffectiveRightBoundaryPx(triggerEl, viewportRight);
    const safeRight = contentRight - VIEWPORT_MARGIN;

    if (contentSized) {
        const layoutW =
            dropdownEl?.offsetWidth ||
            resolveContentDropdownMinWidth(measuredW, dropdownMinWidth);
        let left = triggerRect.left;
        const minLeft = VIEWPORT_MARGIN;
        if (left < minLeft) {
            left = minLeft;
        }
        if (layoutW > 0 && left + layoutW > safeRight) {
            left = Math.max(minLeft, safeRight - layoutW);
        }
        return {
            top,
            left,
            contentSized: true,
            viewportMaxWidth: Math.max(DROPDOWN_CONTENT_MIN_WIDTH, safeRight - left),
        };
    }

    const fitW = Math.max(dropdownMinWidth, measuredW || 0);
    const minLeft = VIEWPORT_MARGIN;
    const maxLeft = safeRight - fitW;

    let left = triggerRect.left;
    if (triggerRect.left + fitW > safeRight) {
        left = triggerRect.right - fitW;
    }
    if (maxLeft >= minLeft) {
        left = Math.max(minLeft, Math.min(left, maxLeft));
    } else {
        left = minLeft;
    }
    if (left + fitW > safeRight && maxLeft >= minLeft) {
        left = Math.max(minLeft, Math.min(triggerRect.right - fitW, maxLeft));
    }

    return {top, left};
}
